import {
  computed,
  onMounted,
  onBeforeUnmount,
  onUnmounted,
  ref,
  watch,
  nextTick,
  provide,
} from "vue";
import { useStore } from "vuex";
import ChatMessage from "./ChatMessage/ChatMessage";
import ChatSendButton from "./ChatSendButton/ChatSendButton";
import ChatWindow from "./ChatWindow/ChatWindow";
import debounce from "foundation/js/debounce";
import { isMobileDevice } from "foundation/js/is-mobile-device";

export default {
  name: "ChatPanel",
  components: {
    ChatWindow,
    ChatMessage,
    ChatSendButton
  },
  props: {
    labels: {
      type: Object,
    },
    color: {
      type: String
    },
    ready: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const store = useStore();
    const message = ref(undefined);
    const chatInputRef = ref(null);
    const introChatButtonRef = ref(undefined);
    const chatWindowRef = ref(null);
    const isSendingMessage = ref(false);
    const windowScrolled = ref(false);
    const permanentlyCollapseStartChatButton = ref(false);
    const elementContainerRef = ref(undefined);
    const bannerVisible = ref(false);
    const breakingNewsBanner = ref(undefined);
    const observer = ref(undefined);

    function updateChatPanelOffset() {
      const banner = breakingNewsBanner.value || document.querySelector(".nyk-breaking-news");
      const menu = document.querySelector(".nyk-component--menu");

      if (!banner || !menu) return;

      const rect = banner.getBoundingClientRect();
      const absoluteBottom = rect.bottom + window.scrollY + menu.offsetHeight;
      document.documentElement.style.setProperty('--chat-banner-offset', `${absoluteBottom}px`);
    }

    function setupBannerObserver() {
      breakingNewsBanner.value = document.querySelector(".nyk-breaking-news")
      const menu = document.querySelector(".nyk-component--menu")

      if (!breakingNewsBanner.value || !menu) return

      var options = {
        root: null,
        threshold: 0,
      }

      observer.value = new IntersectionObserver(([entry]) => {
        bannerVisible.value = entry.isIntersecting
        updateChatPanelOffset();
      }, options);

      observer.value.observe(breakingNewsBanner.value);
    }

    function handleResize() {
      updateChatPanelOffset();
    }

    const debouncedHandleResize = debounce(handleResize, 100);

    function addResizeEventListener() {
      window.addEventListener('resize', debouncedHandleResize);
    }

    function removeResizeEventListener() {
      window.removeEventListener('resize', debouncedHandleResize);
    }

    function getLabel(label) {
      const returnLabel = store.state.chatPanel.labels[label];
      if (!returnLabel) console.error("Label does not exist", label);
      return returnLabel ? returnLabel : `[${label}]`;
    }

    function openChatWindow() {
      store.commit("chatPanel/set_chat_window_open", true);
    }

    const maxCharacters = computed(() => {
      return store.state.chatPanel.conversation?.maxInputChars ?? 110;
    });
    const chatWindowOpen = computed(() => {
      return store.state.chatPanel.chatWindowOpen;
    });

    const messages = computed(() => {
      const messages = store.state.chatPanel.messages;
      return messages ? messages : [];
    });

    const isAwaitingResponse = computed(() => {
      return store.state.chatPanel.isAwaitingResponse;
    });

    async function sendMessage() {
      if (sendMessageDisabled.value) return false;
      try {
        const newMessage = message.value;
        message.value = "";
        isSendingMessage.value = true;
        await store.dispatch("chatPanel/sendMessage", newMessage);
      } finally {
        isSendingMessage.value = false;
      }
    }
    function messageAuthorIsUser(message) {
      if (!message || !message.source) return false;
      return message.source === "client";
    }

    function getMessageAuthorLabel(message) {
      if (!message || !message.source) return "";
      return message.source === "bot" && message.isHumanAgent
        ? store.state.chatPanel.labels["authorLabelCustomerService"]
        : message.source === "bot"
          ? store.state.chatPanel.labels["authorLabelCustomerBot"]
          : "";
    }

    function feedbackButtonIsActive(buttonValue, id) {
      const messageFeedback = store.state.chatPanel.messageFeedback.find(
        (message) => id === message.id
      );
      if (messageFeedback === undefined) return "";
      return messageFeedback.value === buttonValue ? true : false;
    }

    const isLoadingConversation = computed(() => {
      return store.state.chatPanel.isAwaitingResponse;
    });

    const menuOverlayShown = computed(() => {
      return (
        store.state.menu.searchOverlayShown ||
        store.state.menu.submenuOpen ||
        store.state.menu.otherSitesDropdownShown ||
        store.state.menu.languageDropdownShown ||
        store.state.menu.topLevelPagesDropdownShown
      );
    });

    const inputDisabled = computed(() => {
      return (
        store.state.chatPanel.connectionError ||
        store.getters["chatPanel/isConversationExpired"]
      );
    });

    const sendMessageDisabled = computed(() => {
      return (
        isLoadingConversation.value ||
        !message.value ||
        message.value.length === 0
      );
    });

    function sendMessageFeedback(value, id) {
      store.dispatch("chatPanel/sendMessageFeedback", {
        id: id,
        feedback: value,
      });
    }

    function handleAction(actionId) {
      store.dispatch("chatPanel/sendActionId", { actionId });
    }

    function handleExternalLink(payload) {
      store.dispatch("chatPanel/sendExternalLink", payload);
    }

    function setWindowScrolled() {
      windowScrolled.value = window.scrollY > 0;
    }

    const debouncedSetWindowScrolled = debounce(setWindowScrolled, 10);

    const chatIntroButtonCollapsed = computed(() => {
      if (permanentlyCollapseStartChatButton.value) return true;
      return windowScrolled.value;
    });

    function collapseStartChatButton() {
      permanentlyCollapseStartChatButton.value = true;
    }

    function handleFeedback(type, id) {
      let feedbackValue;
      if (type === "positive") {
        feedbackValue = feedbackButtonIsActive("positive", id)
          ? "remove-positive"
          : "positive";
      } else if (type === "negative") {
        feedbackValue = feedbackButtonIsActive("negative", id)
          ? "remove-negative"
          : "negative";
      }
      sendMessageFeedback(feedbackValue, id);
    }

    const chatExpired = computed(() => {
      return store.state.chatPanel.conversation?.state?.expired;
    });

    function showFeedbackButtons(message) {
      if (chatExpired || !message || !message.source) return false;
      return message.source !== "client" && message.source !== "system";
    }

    function addScrollEventListener() {
      window.addEventListener(
        "scroll",
        debouncedSetWindowScrolled
      );
      setWindowScrolled();
    }

    function removeScrollEventListener() {
      window.removeEventListener(
        "scroll",
        debouncedSetWindowScrolled
      );
    }

    watch(chatWindowOpen, async (opened) => {
      if (opened && !isMobileDevice()) {
        await nextTick();
        chatInputRef.value.focus();
      }
    });

    // Navigation loops
    const handleLoopTrap = (e, forward) => {
      const first = chatWindowRef.value.$el.querySelector(
        '[data-order="first"]'
      );
      const last = chatWindowRef.value.$el.querySelector('[data-order="last"]');
      if (forward) {
        e.preventDefault();
        first?.focus();
      } else if (!forward) {
        e.preventDefault();
        last?.focus();
      }
    };

    const handleKeydown = (e) => {
      const current = e.target;
      const order = current.dataset.order;
      if (e.key === "Tab") {
        if (order === "last") {
          if (e.shiftKey) {
            return false;
          } else {
            handleLoopTrap(e, true);
          }
        } else if (order === "first") {
          if (e.shiftKey) {
            handleLoopTrap(e, false);
          } else {
            return false;
          }
        }
      }
      if (e.key === "Enter") {
        sendMessage();
      }
    };

    provide("handleKeydown", handleKeydown);


    onMounted(() => {
      setupBannerObserver();
      addScrollEventListener();
      addResizeEventListener();

      store.dispatch('chatPanel/initializeChatPanel');
      store.dispatch("chatPanel/openChatAndHandleActionIdFromQueryParam")
    });

    onBeforeUnmount(() => {
      if (observer.value && breakingNewsBanner.value) {
        observer.value.unobserve(breakingNewsBanner.value); observer.value.disconnect()
      }
    })

    onUnmounted(() => {
      removeResizeEventListener();
      removeScrollEventListener();
    });

    return {
      handleKeydown,
      menuOverlayShown,
      elementContainerRef,
      inputDisabled,
      chatIntroButtonCollapsed,
      collapseStartChatButton,
      feedbackButtonIsActive,
      chatInputRef,
      handleFeedback,
      handleAction,
      handleExternalLink,
      chatWindowOpen,
      isAwaitingResponse,
      showFeedbackButtons,
      messageAuthorIsUser,
      maxCharacters,
      openChatWindow,
      sendMessage,
      isSendingMessage,
      getMessageAuthorLabel,
      introChatButtonRef,
      isLoadingConversation,
      sendMessageDisabled,
      message,
      chatWindowRef,
      messages,
      bannerVisible,
    };
  },
};